import { useMemo, useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { Quarter, Product, SubProduct, Status } from '@/types';

export interface DashboardFilterState {
  quarter?: string;
  productId?: string;
  subProductId?: string;
  statusId?: string;
}

interface DashboardFiltersProps {
  quarters: Quarter[];
  products: Product[];
  statuses: Status[];
  filters: DashboardFilterState;
  onFiltersChange: (filters: DashboardFilterState) => void;
}

interface DropdownProps {
  label: string;
  value?: string;
  options: { value: string; label: string }[];
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
}

function Dropdown({ label, value, options, onChange, disabled }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => o.value === value);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md transition-colors min-w-[120px] ${
          disabled
            ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
            : value
              ? 'bg-blue-50 border-blue-300 text-blue-700'
              : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
        }`}
      >
        <span className="flex-1 text-left truncate">
          {selectedOption ? selectedOption.label : label}
        </span>
        {value && !disabled ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onChange(undefined);
            }}
            className="p-0.5 hover:bg-blue-200 rounded"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        ) : (
          <ChevronDownIcon
            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-60 overflow-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No options</div>
          ) : (
            options.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                  option.value === value ? 'bg-blue-50 text-blue-700 font-medium' : ''
                }`}
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function DashboardFilters({
  quarters,
  products,
  statuses,
  filters,
  onFiltersChange,
}: DashboardFiltersProps) {
  // Get sub-products based on selected product
  const subProducts = useMemo(() => {
    if (!filters.productId) return [];
    const product = products.find((p) => p.id === filters.productId);
    return product?.subProducts || [];
  }, [products, filters.productId]);

  // Quarter options
  const quarterOptions = useMemo(() => {
    return quarters.map((q) => ({
      value: q.key,
      label: q.fullLabel,
    }));
  }, [quarters]);

  // Product options
  const productOptions = useMemo(() => {
    return products.map((p) => ({
      value: p.id,
      label: p.name,
    }));
  }, [products]);

  // Sub-product options
  const subProductOptions = useMemo(() => {
    return subProducts.map((sp: SubProduct) => ({
      value: sp.id,
      label: sp.name,
    }));
  }, [subProducts]);

  // Status options
  const statusOptions = useMemo(() => {
    return statuses.map((s) => ({
      value: s.id,
      label: s.name,
    }));
  }, [statuses]);

  const handleQuarterChange = (value: string | undefined) => {
    onFiltersChange({ ...filters, quarter: value });
  };

  const handleProductChange = (value: string | undefined) => {
    // Clear sub-product when product changes
    onFiltersChange({ ...filters, productId: value, subProductId: undefined });
  };

  const handleSubProductChange = (value: string | undefined) => {
    onFiltersChange({ ...filters, subProductId: value });
  };

  const handleStatusChange = (value: string | undefined) => {
    onFiltersChange({ ...filters, statusId: value });
  };

  const hasFilters =
    filters.quarter || filters.productId || filters.subProductId || filters.statusId;

  const clearFilters = () => {
    onFiltersChange({});
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <span className="text-sm font-medium text-gray-500">Filter by:</span>

      <Dropdown
        label="Quarter"
        value={filters.quarter}
        options={quarterOptions}
        onChange={handleQuarterChange}
      />

      <Dropdown
        label="Product"
        value={filters.productId}
        options={productOptions}
        onChange={handleProductChange}
      />

      <Dropdown
        label="Sub-product"
        value={filters.subProductId}
        options={subProductOptions}
        onChange={handleSubProductChange}
        disabled={!filters.productId}
      />

      <Dropdown
        label="Status"
        value={filters.statusId}
        options={statusOptions}
        onChange={handleStatusChange}
      />

      {hasFilters && (
        <button
          onClick={clearFilters}
          className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
        >
          Clear all
        </button>
      )}
    </div>
  );
}
